import React, { useEffect, useRef } from "react";
import Button from "@/core/components/button/Button";
import { useUnit } from "effector-react";
import { linkedinCodeReceived, $authStore } from "../store/auth";
import { imageList } from "@/core/utils/imageList";

interface LinkedInLoginButtonProps {
  isLoginPage?: boolean;
}

const LinkedInLoginButton = ({ isLoginPage }: LinkedInLoginButtonProps) => {
  const { loading, error } = useUnit($authStore);
  const handleLinkedInCode = useUnit(linkedinCodeReceived);
  const hasProcessedCode = useRef(false);

  useEffect(() => {
    // Prevent double execution
    if (hasProcessedCode.current) {
      //console.log("Already processed code, skipping");
      return;
    }

    // Check if returning from full-page redirect with code
    const storedCode = sessionStorage.getItem("linkedin_auth_code");
    if (storedCode) {
      //console.log("Found stored LinkedIn code from redirect:", storedCode);
      hasProcessedCode.current = true;
      sessionStorage.removeItem("linkedin_auth_code");
      handleLinkedInCode({ code: storedCode });
      return;
    }

    // Listen for messages from the popup
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "linkedin-auth" && event.data?.code) {
        if (hasProcessedCode.current) {
          //console.log("Code already processed, ignoring duplicate");
          return;
        }

        //console.log("Received LinkedIn code from popup:", event.data.code);
        hasProcessedCode.current = true;
        handleLinkedInCode({ code: event.data.code });
      }

      if (event.data?.type === "linkedin-auth-error") {
        console.error("LinkedIn auth error:", event.data.error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleLinkedInCode]);

  const handleLinkedInLogin = () => {
    // Reset the flag when starting a new login
    hasProcessedCode.current = false;

    const clientId = "7751n85w3dhfye";
    const redirectUri =
      window.location.origin + "/user/login/linkedin/callback";
    const scope = "r_liteprofile r_emailaddress";
    const state = Math.random().toString(36).substring(7);

    // Store state in localStorage (accessible across windows in same origin)
    localStorage.setItem("linkedin_oauth_state", state);

    // Build auth URL
    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&state=${state}&scope=${encodeURIComponent(scope)}`;

    // Try to open in popup first
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      authUrl,
      "LinkedInLogin",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
    );

    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      console.warn("Popup blocked! Falling back to full page redirect.");
      // Fallback to full page redirect
      window.location.href = authUrl;
    } else {
      // Focus the popup
      popup.focus();
      //console.log("LinkedIn popup opened successfully");
    }
  };

  return (
    <div className={isLoginPage ? "social-login mb-8" : "social-login"}>
      <Button
        className="social-btn linkedin-btn"
        variant="secondary"
        size="normal"
        outlined
        onClick={handleLinkedInLogin}
        loading={loading}
        disabled={loading}
      >
        <span className="social-icon">
          <img src={imageList.linkedinIcon} alt="linkedin icon" />
        </span>
        {isLoginPage && <span>Sign in with LinkedIn</span>}
      </Button>

      {/* {error && <div className="error-toast">{error}</div>} */}
    </div>
  );
};

export default LinkedInLoginButton;
